/**
 * A compare_scene result: a verdict about what the product says it DREW,
 * element by element, rather than about a picture.
 *
 * The step compares two scenes by structural path and lists what differs as
 * path, field, the approved value and today's. That table is the point: a
 * changed letter in a scrolling list is a row here and nothing at all in a
 * golden that had to mask the list. The step keeps its record small - it lists
 * as many rows as fit and counts the rest - and the full difference stays
 * beside the run on the bench, named by `detail`.
 *
 * Recognised by `oracle: "scene"` and checked BEFORE the picture comparison:
 * the day a picture result also carries a scene field, recognition by the
 * absence of a key would send it down the wrong branch without a word.
 */
import { useState } from "react";
import i18n from "../i18n";
import { Chip, Mono, Rows } from "./StepParts";
import GoldenEnvironment from "./GoldenEnvironment";

type SceneChange = { path?: string; field?: string; golden?: unknown; now?: unknown };

/** How many changed fields show before "show all". The run page was cut from
 *  3538 px to 1038 px in the UX pass; one scene with hundreds of rows would
 *  hand that back. */
export const ROWS_SHOWN = 20;

/** What the framework writes for a field one side does not have (the product
 *  leaves fields at their default out). Shown as its own thing, never as text. */
const ABSENT = "<absent>";

export function isScene(b: unknown): b is Record<string, unknown> {
  return (
    !!b && typeof b === "object" && !Array.isArray(b) &&
    (b as Record<string, unknown>).oracle === "scene"
  );
}

function strings(v: unknown): string[] {
  return Array.isArray(v) ? v.map(String) : [];
}

/** Characters that take no space on screen: format controls, zero-width
 *  spaces, variation selectors. A layout label ending in U+FE0F read exactly
 *  like one without it (BAWU v331, 2026-09-10) - here it must not. */
const INVISIBLE = /[\u00AD\u180E\u200B-\u200F\u202A-\u202E\u2060-\u2064\uFE00-\uFE0F\uFEFF]/g;

/** A value as a reader must see it: null, "" and absent are three different
 *  findings, a string shows its quotes and its invisible characters, and a
 *  structure is compact JSON. */
export function sceneValue(v: unknown): { text: string; kind: "absent" | "null" | "value" } {
  if (v === ABSENT || v === undefined) return { text: i18n.t("runDetail.scene.absent"), kind: "absent" };
  if (v === null) return { text: "null", kind: "null" };
  if (typeof v === "string") {
    const shown = JSON.stringify(v).replace(
      INVISIBLE,
      (c) => `\\u${c.charCodeAt(0).toString(16).toUpperCase().padStart(4, "0")}`,
    );
    return { text: shown, kind: "value" };
  }
  if (typeof v === "number" || typeof v === "boolean") return { text: String(v), kind: "value" };
  const s = JSON.stringify(v);
  return { text: s.length > 200 ? s.slice(0, 199) + "…" : s, kind: "value" };
}

function Value({ v, muted }: { v: unknown; muted?: boolean }) {
  const { text, kind } = sceneValue(v);
  const tone =
    kind === "absent"
      ? "italic text-slate-400"
      : muted
        ? "text-slate-500"
        : undefined;
  return <span className={tone}>{text}</span>;
}

function PathList({ title, paths }: { title: string; paths: string[] }) {
  if (paths.length === 0) return null;
  return (
    <div>
      <div className="text-xs text-slate-500">{title}</div>
      <Mono>{paths.join("\n")}</Mono>
    </div>
  );
}

export default function SceneResult({
  body,
  expected,
}: {
  body: Record<string, unknown>;
  expected?: boolean;
}) {
  const t = i18n.t.bind(i18n);
  const [all, setAll] = useState(false);
  const changed: SceneChange[] = Array.isArray(body.changed)
    ? (body.changed as SceneChange[])
    : [];
  const added = strings(body.added);
  const removed = strings(body.removed);
  const ignored = strings(body.ignored);
  const ignoredFields = strings(body.ignored_fields);
  const compared = typeof body.compared === "number" ? body.compared : 0;
  const differences = typeof body.differences === "number" ? body.differences : 0;
  const notListed = typeof body.not_listed === "number" ? body.not_listed : 0;
  const recorded = body.recorded === true;
  const prov =
    body.golden_provenance && typeof body.golden_provenance === "object" &&
    !Array.isArray(body.golden_provenance)
      ? (body.golden_provenance as Record<string, unknown>)
      : null;
  // A comparison of nothing is not a pass, whatever `pass` says: the same
  // family as a run whose every scenario was gated out.
  const nothing = !recorded && compared === 0;
  // Red means the step did not do what it claimed, as for a picture: a case
  // that asserts the miss (DOS-BAWU-SCENE-DISTINCT) reads neutral.
  const ok = nothing ? undefined : body.pass === true ? true : expected ? undefined : false;
  const rows = all ? changed : changed.slice(0, ROWS_SHOWN);

  return (
    <div className="mt-0.5 space-y-1" data-testid="scene-result">
      <div className="flex flex-wrap items-center gap-1">
        <Chip ok={ok}>
          {recorded
            ? t("runDetail.scene.elements", { count: compared })
            : nothing
              ? t("runDetail.scene.nothingCompared")
              : t("runDetail.scene.verdict", { count: differences, compared })}
        </Chip>
        {body.pass === false && expected && <Chip>{t("runDetail.theMiss")}</Chip>}
        <span className="break-all font-mono text-[11px] text-slate-500">
          {String(body.golden)}
        </span>
        {recorded && <Chip>{t("runDetail.recorded")}</Chip>}
        {typeof body.within === "string" && (
          <Chip>{t("runDetail.scene.within", { path: body.within })}</Chip>
        )}
        {ignored.length > 0 && (
          <Chip>{t("runDetail.scene.ignored", { list: ignored.join(", ") })}</Chip>
        )}
        {ignoredFields.length > 0 && (
          <Chip>{t("runDetail.scene.ignoredFields", { list: ignoredFields.join(", ") })}</Chip>
        )}
        {typeof body.settled_after === "number" && (
          <Chip>{t("runDetail.scene.settled", { count: body.settled_after })}</Chip>
        )}
      </div>

      {rows.length > 0 && (
        <table className="w-full table-fixed text-[11px]">
          <thead>
            <tr className="text-slate-400">
              <th className="w-2/5 text-left font-normal">{t("runDetail.scene.colPath")}</th>
              <th className="w-1/6 text-left font-normal">{t("runDetail.scene.colField")}</th>
              <th className="text-left font-normal">{t("runDetail.scene.colGolden")}</th>
              <th className="text-left font-normal">{t("runDetail.scene.colNow")}</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {rows.map((c, i) => (
              <tr key={`${c.path}:${c.field}:${i}`} className="align-top">
                <td className="break-all pr-2">{c.path ?? t("common.dash")}</td>
                <td className="pr-2">{c.field ?? t("common.dash")}</td>
                <td className="break-words pr-2"><Value v={c.golden} muted /></td>
                <td className="break-words"><Value v={c.now} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {changed.length > ROWS_SHOWN && (
        <button
          type="button"
          className="text-[11px] text-sky-700 underline dark:text-sky-400"
          onClick={() => setAll((v) => !v)}
        >
          {all
            ? t("runDetail.scene.showFewer")
            : t("runDetail.scene.showAll", { count: changed.length })}
        </button>
      )}

      <PathList title={t("runDetail.scene.added")} paths={added} />
      <PathList title={t("runDetail.scene.removed")} paths={removed} />

      {notListed > 0 && (
        <div className="text-[11px] text-slate-500">
          {t("runDetail.scene.notListed", {
            count: notListed,
            detail: String(body.detail ?? t("common.dash")),
          })}
        </div>
      )}

      <GoldenEnvironment body={body} />
      {prov && (
        <div>
          <div className="text-xs text-slate-500">
            {t("runDetail.goldenRecordedFrom")}
          </div>
          <Rows obj={prov} />
        </div>
      )}
    </div>
  );
}
